// Copyright (c) 2026 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TimelineDateIndicator } from '@/components/timeline-date-indicator';
import { CloseRounded } from '@/constants/hardfin-standard-icons';
import { TimelineProvider } from '@/contexts/timeline-provider';
import { ProjectLineItemGanttBar } from '@/project/components/project-line-item-gantt-bar';
import { GanttItem } from '@/project/components/project-line-item-gantt-types';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { AxisScale } from '@visx/axis';
import { scaleTime } from '@visx/scale';
import { Line } from '@visx/shape';
import { DateTime, Interval } from 'luxon';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

interface ResolvedGanttItem extends GanttItem {
  start: DateTime;
  end: DateTime | null;
}

const AXIS_HEIGHT = 36;
const BAR_HEIGHT = 22;
const BAR_MARGIN = 6;
const PADDING_X = 16;
const MIN_CHART_WIDTH = 400;

interface Props {
  items: GanttItem[];
  groupColor: string;
  onClose?: () => void;
}

/**
 * Formats a tick date for the Gantt axis. Shows month abbreviation and
 * 2-digit year (e.g. "Sep '24") so multi-year ranges are readable.
 */
function formatGanttTick(dateTime: DateTime): string {
  return dateTime.toFormat("LLL ''yy");
}

/**
 * Renders month tick labels and vertical grid lines along the top of
 * the chart. Uses month-aligned ticks with year-aware labels.
 */
function GanttAxis(axisProps: {
  scale: AxisScale<number>;
  width: number;
  interval: Interval;
  axisHeight: number;
  chartHeight: number;
}) {
  const theme = useTheme();
  const ticks = axisProps.interval.splitBy({ months: 1 });

  return (
    <>
      <Line
        stroke={theme.palette.divider}
        strokeWidth={1}
        from={{ x: 0, y: axisProps.axisHeight }}
        to={{ x: axisProps.width, y: axisProps.axisHeight }}
      />
      {ticks.map((tick) => {
        if (!tick.start) return null;
        const x = axisProps.scale(tick.start.toJSDate()) ?? 0;
        if (x < PADDING_X || x > axisProps.width - PADDING_X) return null;

        return (
          <g key={tick.start.toISO()}>
            <Line
              stroke={theme.palette.divider}
              strokeWidth={0.5}
              from={{ x, y: axisProps.axisHeight }}
              to={{ x, y: axisProps.chartHeight }}
              strokeDasharray="2 3"
              strokeOpacity={0.4}
            />
            <foreignObject
              x={x - 1}
              y={4}
              width={60}
              height={axisProps.axisHeight - 8}
            >
              <Typography
                variant="caption"
                fontSize="0.65rem"
                color="text.secondary"
                whiteSpace="nowrap"
              >
                {formatGanttTick(tick.start)}
              </Typography>
            </foreignObject>
          </g>
        );
      })}
    </>
  );
}

/**
 * SVG chart area for the line item Gantt visualization. Renders a time axis,
 * bars for each item, and a today indicator. Sizes itself to the parent
 * container width to avoid flicker.
 */
export function ProjectLineItemGanttChart(props: Props) {
  const theme = useTheme();
  const today = useMemo(() => DateTime.now().startOf('day'), []);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const measure = useCallback(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.clientWidth);
    }
  }, []);

  useLayoutEffect(() => {
    measure();
    const observer = new ResizeObserver(() => {
      measure();
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [measure]);

  // Items with no start date are excluded. Items with no end date are treated
  // as open-ended (rendered with a faded trail to the chart edge).
  const validItems = useMemo(
    (): ResolvedGanttItem[] =>
      props.items
        .filter((item) => item.start !== null)
        .map((item) => ({
          ...item,
          start: item.start as DateTime,
          end: item.end,
        })),
    [props.items]
  );

  // Chart date range is based on the active date range of all items + 1 month
  // padding on each side. Today is irrelevant for range calculation.
  const { minDate, maxDate, interval } = useMemo(() => {
    if (validItems.length === 0) {
      const fallbackStart = today.startOf('month').minus({ months: 1 });
      const fallbackEnd = today.startOf('month').plus({ months: 2 });
      return {
        minDate: fallbackStart,
        maxDate: fallbackEnd,
        interval: Interval.fromDateTimes(fallbackStart, fallbackEnd),
      };
    }
    // Collect all known dates (starts + ends) so the chart range covers
    // everything even when phases overlap or have invalid ordering.
    const allDates: DateTime[] = validItems.map((item) => item.start);
    for (const item of validItems) {
      if (item.end !== null) {
        allDates.push(item.end);
      }
    }
    const min = DateTime.min(...allDates)
      .startOf('month')
      .minus({ months: 1 });
    const max = DateTime.max(...allDates)
      .endOf('month')
      .plus({ months: 1 })
      .startOf('month');
    // Guard against invalid interval when all dates collapse to the same point
    const safeMax = max <= min ? min.plus({ months: 2 }) : max;
    return {
      minDate: min,
      maxDate: safeMax,
      interval: Interval.fromDateTimes(min, safeMax),
    };
  }, [validItems, today]);

  const chartWidth = Math.max(containerWidth, MIN_CHART_WIDTH);
  const chartEndX = chartWidth - PADDING_X;
  const chartHeight =
    AXIS_HEIGHT +
    BAR_MARGIN +
    Math.max(validItems.length, 1) * (BAR_HEIGHT + BAR_MARGIN);

  const dateScale = scaleTime({
    range: [PADDING_X, chartEndX],
    domain: [minDate.toJSDate(), maxDate.toJSDate()],
  });

  return (
    <Box ref={containerRef} sx={{ width: '100%' }}>
      {validItems.length > 0 && containerWidth > 0 && (
        <svg width={chartWidth} height={chartHeight}>
          <TimelineProvider>
            <GanttAxis
              scale={dateScale}
              width={chartWidth}
              interval={interval}
              axisHeight={AXIS_HEIGHT}
              chartHeight={chartHeight}
            />
            {props.onClose && (
              <foreignObject x={chartWidth - 36} y={4} width={28} height={28}>
                <IconButton
                  size="small"
                  onClick={props.onClose}
                  sx={{ p: 0.25 }}
                >
                  <CloseRounded fontSize="small" />
                </IconButton>
              </foreignObject>
            )}
            <TimelineDateIndicator
              y={AXIS_HEIGHT}
              height={chartHeight}
              scale={dateScale}
              dateTime={today}
              color={theme.palette.primary.light}
            />
            {validItems.map((item, index) => {
              const y =
                AXIS_HEIGHT + BAR_MARGIN + index * (BAR_HEIGHT + BAR_MARGIN);
              const quantity = item.quantity ?? 0;
              const itemLabel = item.label;
              const label = `${quantity} × ${itemLabel}`;
              const startLabel = item.start.toLocaleString({
                month: 'short',
                day: 'numeric',
              });
              const endLabel =
                item.end !== null
                  ? item.end.toLocaleString({
                      month: 'short',
                      day: 'numeric',
                    })
                  : 'open-ended';
              const dateLabel = `${startLabel} — ${endLabel}`;
              const tooltipText = `${quantity} × ${itemLabel}\n${dateLabel}`;

              return (
                <ProjectLineItemGanttBar
                  key={item.id}
                  y={y}
                  height={BAR_HEIGHT}
                  scale={dateScale}
                  start={item.start}
                  end={item.end}
                  chartEndX={chartEndX}
                  label={label}
                  dateLabel={dateLabel}
                  tooltipText={tooltipText}
                  isInactive={quantity === 0}
                  color={props.groupColor}
                />
              );
            })}
          </TimelineProvider>
        </svg>
      )}
    </Box>
  );
}
